// ABOUTME: Material UI dashboard card component showing weekly meltdown tracking with developmental comparisons
// ABOUTME: Uses Recharts for age range visualization and displays current week vs historical average data

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  ReferenceArea
} from 'recharts';
import { getMobileChartMargins, MOBILE_BREAKPOINT_QUERY } from '../../utils/mobileOptimization';
import { filterDevelopmentalRanges, getAgeLabel, getAgeRangeText, getFirstName } from '../../utils/developmentalRangeUtils';

const MeltdownTrackerCard = ({ data, error = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const chartMargins = getMobileChartMargins('composedChart', isMobile);

  // Loading state
  if (!data && !error) {
    return (
      <Card role="region" aria-label="Meltdown tracker">
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card role="region" aria-label="Meltdown tracker">
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const {
    currentWeek,
    historicalAverage,
    developmentalRanges,
    childName,
    childAge
  } = data;

  // Filter developmental ranges to always show exactly 3 ranges
  const { filteredRanges, showDataInColumn } = filterDevelopmentalRanges(
    developmentalRanges,
    childAge,
    childName,
    'minPerWeek'
  );

  // Prepare chart data from filtered developmental ranges
  const chartData = filteredRanges.map((range, index) => {
    const isCustomChildRange = range.isCustomChildRange === true;
    const shouldShowData = index === showDataInColumn;

    return {
      ageRange: getAgeLabel(range),
      developmentalMin: isCustomChildRange ? null : range.minPerWeek,
      developmentalMax: isCustomChildRange ? null : range.maxPerWeek,
      thisWeekCount: shouldShowData ? currentWeek.meltdownCount : null,
      isCurrentAge: range.isCurrentAge,
      isCustomChildRange: isCustomChildRange,
      developmentalFill: range.isCurrentAge ? theme.palette.error.light : theme.palette.grey[300],
      currentFill: theme.palette.error.main
    };
  });

  const maxChartValue = Math.max(
    ...filteredRanges.map(r => r.maxPerWeek),
    historicalAverage,
    currentWeek.meltdownCount
  ) + 2;

  // Format historical average to 1 decimal place
  const formattedAverage = parseFloat(historicalAverage.toFixed(1));

  // Get first name and age range text
  const firstName = getFirstName(childName);
  const currentAgeRange = filteredRanges.find(range => range.isCurrentAge);
  const ageRangeText = getAgeRangeText(currentAgeRange, 'minPerWeek', 'per week', 0);

  return (
    <Card role="region" aria-label="Meltdown tracker">
      <CardContent>
        {/* Card Title */}
        <Typography variant="h5" component="h2" gutterBottom textAlign="center" sx={{ fontWeight: 'bold', mb: 3 }}>
          Weekly Meltdown vs Developmental Averages
        </Typography>
        
        {/* 50/50 Split Layout */}
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} sx={{ mb: 3 }}>
          {/* Left Side - Current Week Count */}
          <Box flex={1} textAlign="center" sx={{ pr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              This Week:
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Typography
                variant="h2"
                component="div"
                sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', sm: '3rem' }, color: theme.palette.error.main }}
              >
                {currentWeek.meltdownCount}
              </Typography>
              <Typography 
                variant="h5" 
                color="textSecondary" 
                sx={{ alignSelf: 'center', textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {currentWeek.meltdownCount === 1 ? 'Meltdown' : 'Meltdowns'}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
              Day {currentWeek.daysIntoWeek} of 7
            </Typography>
          </Box>
          
          {/* Right Side - Chips */}
          <Box flex={1} display="flex" flexDirection="column" justifyContent="center" gap={2} sx={{ pl: { xs: 0, sm: 2 } }}>
            {/* Position 1: Age Average Chip (NEW) */}
            <Chip
              data-testid="chip-age-average"
              label={`Avg. for Age: ${ageRangeText}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.grey[300],
                color: theme.palette.grey[800],
                border: `1px solid ${theme.palette.grey[600]}`
              }}
            />

            {/* Position 2: 12-Week Average Chip (UPDATED) */}
            <Chip
              data-testid="chip-12-week-average"
              label={`${firstName} 12 Week Avg: ${formattedAverage}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText
              }}
            />

            {/* Position 3: This Week Count Chip (UPDATED) */}
            <Chip
              data-testid="chip-this-week-count"
              label={`${firstName} This Week: ${currentWeek.meltdownCount}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.contrastText
              }}
            />
          </Box>
        </Box>


        {/* Chart */}
        <Box sx={{ width: '100%', height: { xs: 200, sm: 250 }, mb: 1 }}>
          <ResponsiveContainer>
            <ComposedChart
              data={chartData}
              margin={chartMargins}
              barCategoryGap={0}
            >
              <XAxis 
                dataKey="ageRange" 
                tick={{ fontSize: 12, textAnchor: 'middle' }}
                axisLine={{ stroke: theme.palette.grey[800], strokeWidth: 2 }}
                interval={0}
              />
              <YAxis 
                domain={[0, maxChartValue]}
                tick={{ fontSize: 12 }}
                tickCount={maxChartValue + 1}
                interval={0}
                axisLine={{ stroke: theme.palette.grey[800], strokeWidth: 2 }}
              />
              
              {/* Developmental range backgrounds */}
              {chartData.map((entry, index) => {
                // Only show ReferenceArea if there's a goal (skip child's age column if outside ranges)
                if (entry.developmentalMin === null || entry.developmentalMax === null) {
                  return null;
                }

                return (
                  <ReferenceArea
                    key={`range-${index}`}
                    x1={entry.ageRange}
                    x2={entry.ageRange}
                    y1={entry.developmentalMin}
                    y2={entry.developmentalMax}
                    fill={entry.developmentalFill}
                    fillOpacity={0.6}
                    stroke={theme.palette.grey[600]}
                    strokeWidth={1}
                  />
                );
              })}
              
              {/* This week's count bar - Issue #963 */}
              <Bar
                dataKey="thisWeekCount"
                radius={[4, 4, 0, 0]}
                barSize={40}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`this-week-${index}`}
                    fill={entry.isCurrentAge ? theme.palette.error.main : 'transparent'}
                    data-testid={entry.isCurrentAge ? "this-week-count-bar" : undefined}
                    data-value={entry.isCurrentAge ? currentWeek.meltdownCount : undefined}
                  />
                ))}
              </Bar>
              
              {/* 12-week average line - Issue #963: Daily progress blue */}
              <ReferenceLine
                y={historicalAverage}
                stroke={theme.palette.primary.main}
                strokeWidth={3}
                strokeDasharray="5 5"
                data-testid="12-week-average-line"
                data-value={historicalAverage.toString()}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>


        {/* Meltdown Explanation */}
        <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2, fontStyle: 'italic' }}>
          Meltdowns are tracked as square events in your daily behavior log
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MeltdownTrackerCard;